import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { Entity, addressToString } from '@debbie/common/dist/index.js';
import { IntentType } from '@debbie/common/dist/intents/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { toEditorMentions } from '@debbie/common/dist/templates/suggestions.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as templatesAPI from '@debbie/cortex/dist/api/templates.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { downloadFile } from '@debbie/cortex/dist/files.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useNewInteraction } from '@debbie/cortex/dist/newInteractions/index.js';
import { letterInitialState } from '@debbie/cortex/dist/newInteractions/types.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import { useTemplatesMap } from '@debbie/cortex/dist/templates/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { APIBASE } from '../../config.js';
import { errorNotification } from '../../utils/errors.js';
import { parseText } from '../../utils/templates.js';
import { File, FileDropzone, Input, Intent } from '..//index.js';
import NewEditor from '../editor/Editor.js';
import { isEditorEmpty, useEditorState } from '../editor/hooks.js';
import Download from '../Icons/Download.js';
import AddFromTemplate from './AddFromTemplate.js';
import InteractionFooter, { AddDebtorDocumentsAction, AddIntentAction, AddTemplateAction, AttachAction, FooterAction, PreviewAction, SendButton, } from './InteractionFooter.js';
import { PDFTemplatePreviewModal } from './PDFTemplatePreview.js';
import TemplateSearch from './TemplateSearch.js';
import VisibilityToggle from './VisibilityToggle.js';
const LetterInteraction = (props) => {
    const singleCase = props.case;
    const lang = useSelector((s) => s.lang);
    const [interaction, setInteraction] = useNewInteraction(singleCase.caseId);
    const letter = interaction.letter;
    const uploadedFiles = interaction.uploadedFiles;
    const tenant = useTenant();
    const templatesMapLoadable = useTemplatesMap();
    const templatesMap = h.hasData(templatesMapLoadable)
        ? templatesMapLoadable.data
        : null;
    const selectedTemplate = letter.templateId && templatesMap ? templatesMap[letter.templateId] : null;
    const customerLoader = useCustomer(props.case?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const documentsLoadable = useDocumentsByReference(props.case?.caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const dropzoneRef = useRef(null);
    const contentState = useEditorState(letter.content);
    const [letterPreviewModalOpen, setLetterPreviewModalOpen] = useState(false);
    const [emailTemplateAdderOpen, setEmailTemplateAdderOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const creditorLoadable = useCreditor(props.case?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const contactPerson = getContactPerson(props.customer, props.case);
    const letterTo = contactPerson && contactPerson.details.address && contactPerson.details.name
        ? contactPerson.details.name
        : '';
    const letterAddress = contactPerson && contactPerson.details.address
        ? contactPerson.details.address
        : null;
    const toggleLetterPreviewModal = () => {
        setLetterPreviewModalOpen(!letterPreviewModalOpen);
    };
    const toggleAddTemplateModal = () => {
        setEmailTemplateAdderOpen(!emailTemplateAdderOpen);
    };
    const mentionSuggestions = useMentionSuggestions(TemplateType.LETTER, singleCase.caseGroup?.id || null);
    const sendLetterMutation = useMutation(async () => {
        if (!letterAddress) {
            throw new Error('No address provided');
        }
        if (!collector) {
            throw new Error('Could not find collector');
        }
        const pdfBody = parseText(contentState.html, mentionSuggestions);
        await c.newInteractions.sendLetter(singleCase.caseId, Entity.Case, letter.templateId, pdfBody, (uploadedFiles || []).map((f) => f.fileId), visibleToClients ? 'client' : 'caseworker', letterAddress, letter.intent);
        contentState.overwriteEditorContent(letterInitialState.content);
    });
    const sendLetter = () => {
        sendLetterMutation.mutate();
    };
    const downloadPdf = useMutation((tenantId) => downloadFile(`${APIBASE}/v1/${tenantId}` +
        templatesAPI.pdfPreviewURL(letter.templateId, singleCase.caseId, parseText(contentState.html, mentionSuggestions)), selectedTemplate?.fileName ?? 'letter.pdf'), {
        onError: (err) => errorNotification(lang, err, 'any', lang.errorEntities.file),
    });
    const downloadDocx = useMutation((tenantId) => downloadFile(`${APIBASE}/v1/${tenantId}` +
        templatesAPI.docxPreviewURL(letter.templateId, singleCase.caseId, parseText(contentState.html, mentionSuggestions)), selectedTemplate?.docxFile?.fileName ?? 'letter.docx'), {
        onError: (err) => errorNotification(lang, err, 'any', lang.errorEntities.file),
    });
    const hasBody = () => !isEditorEmpty(contentState.html);
    const documentsVisibleForDebtorToAdd = documents
        ? documents.filter((document) => document.minAccessRole === 'debtor' &&
            (document.signedFile || document.file)?.contentType ===
                'application/pdf' &&
            !uploadedFiles.find((uploadedFile) => uploadedFile.fileId === document.file?.fileId))
        : null;
    return (React.createElement(FileDropzone, { onChange: (file) => {
            setInteraction('uploadedFiles', [...uploadedFiles, file]);
        }, dropzoneRef: dropzoneRef, accept: "application/pdf" },
        React.createElement(TemplateSearch, { interaction: letter, templateType: TemplateType.LETTER, case: singleCase, customer: props.customer, onChange: (state) => {
                contentState.overwriteEditorContent(state);
            } }),
        React.createElement(Input, { disabled: true, placeholder: lang.interactions.contact, value: `${letterTo}${letterAddress && letterAddress.address
                ? ` - ${addressToString(letterAddress)}`
                : ''}` }),
        React.createElement(NewEditor, { mentions: toEditorMentions(mentionSuggestions), disabled: !letter.templateId, mentionSuggestions: mentionSuggestions, onChange: (html) => {
                contentState.onChange(html);
                setInteraction('letter', { ...letter, content: html });
            }, initialContent: contentState.editorContent }),
        React.createElement("div", { style: { marginTop: '20px' } },
            !!(uploadedFiles || [])?.length &&
                (uploadedFiles || []).map((file, i) => (React.createElement(File, { key: file.fileId, file: file, onRemove: () => setInteraction('uploadedFiles', [
                        ...uploadedFiles.slice(0, i),
                        ...uploadedFiles.slice(i + 1),
                    ]), removable: true }))),
            emailTemplateAdderOpen && props.case && customer && (React.createElement(AddFromTemplate, { toggleAddTemplateModal: toggleAddTemplateModal, case: props.case, customer: customer, mentionSuggestions: mentionSuggestions }))),
        props.isDebtor && letter.intent.type !== IntentType.Unknown && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
            React.createElement(Intent, { intent: letter.intent, editable: true, onEdit: (intent) => {
                    setInteraction('letter', { ...letter, intent: intent });
                } }))),
        React.createElement(InteractionFooter, { error: sendLetterMutation.error, primary: React.createElement(React.Fragment, null,
                React.createElement(SendButton, { loading: letter.loading || sendLetterMutation.isLoading, disabled: !letter.templateId || !hasBody(), onClick: sendLetter }, lang.interactions.send),
                React.createElement(PreviewAction, { onClick: toggleLetterPreviewModal, disabled: !letter.templateId || !hasBody() })), actions: React.createElement(React.Fragment, null,
                React.createElement(AttachAction, { onClick: () => dropzoneRef.current?.open() }),
                React.createElement(AddTemplateAction, { onClick: toggleAddTemplateModal }),
                React.createElement(AddDebtorDocumentsAction, { documents: documentsVisibleForDebtorToAdd, onAdd: (files) => setInteraction('uploadedFiles', uploadedFiles.concat(files)) }),
                props.isDebtor && letter.intent.type === IntentType.Unknown && (React.createElement(AddIntentAction, { onAdd: (intent) => setInteraction('letter', { ...letter, intent }) })),
                letter.templateId && tenant && (React.createElement(FooterAction, { disabled: downloadPdf.isLoading, onClick: () => downloadPdf.mutate(tenant.id), title: lang.interactions.download },
                    React.createElement(Download, null),
                    React.createElement("span", null, "PDF"))),
                letter.templateId &&
                    tenant &&
                    selectedTemplate?.docxFile != null && (React.createElement(FooterAction, { disabled: downloadDocx.isLoading, onClick: () => downloadDocx.mutate(tenant.id), title: lang.interactions.downloadDocx },
                    React.createElement(Download, null),
                    React.createElement("span", null, "DOCX"))),
                React.createElement(VisibilityToggle, { visible: visibleToClients, onChange: setVisibleToClients })) }),
        letterPreviewModalOpen && (React.createElement(PDFTemplatePreviewModal, { caseId: singleCase.caseId, templateId: letter.templateId, body: parseText(contentState.html, mentionSuggestions), onToggle: toggleLetterPreviewModal }))));
};
export default LetterInteraction;
//# sourceMappingURL=LetterInteraction.js.map